/**
 * WhatsApp contracts — slice 7, "WhatsApp".
 *
 * Three things here are not email with a different word for "send", and every
 * shape in this file exists because of one of them.
 *
 * **1. Variables are POSITIONAL.** `{{1}}`, `{{2}}` — never `{{ name }}`.
 * {@link parseWaVariableToken} is a second parser standing beside slice 4's
 * {@link import('./template.js').parseTemplateVariableName}, and that
 * duplication is the point: the email parser accepts `{{ extras.Company Name }}`
 * because spreadsheet headers have spaces in them, and accepting that grammar
 * here would produce a submission Meta rejects. A rejection loop against Meta is
 * how a WABA gets flagged, so the grammar is refused in Wend, before the Graph
 * call. What *is* shared is the other half — which contact field a position
 * resolves to reuses the email contract's target grammar exactly, so `extras.`
 * means one thing in this product rather than two.
 *
 * **2. There are no attachments — there is a media HEADER.** One optional
 * header object per template. Sharing the word "attachment" between the two
 * would still be the mistake — different features, different lifetimes,
 * different rules about what may be in the file.
 *
 * SUPERSEDED BY SLICE 9, and left here because a reader who finds only the new
 * sentence will not see what changed. This paragraph used to end *"`template_assets`
 * (bytes we store and serve for an email) is untouched by this file; a media
 * header is a handle Meta stores"*. The first half is no longer true and the
 * second half was never the whole story. Slice 9 keeps the bytes in
 * `template_assets` under the `wa_header_media` kind **as well as** minting
 * Meta's handle from them, because a handle is only valid for template
 * *creation* and cannot be re-minted from nothing: a rejected template whose
 * bytes we did not keep is a template that can never be resubmitted. The handle
 * is stored beside the asset, never instead of it. `./whatsapp-media.js` owns
 * that half; this file still owns the *document*, and {@link waMediaHeaderSchema}
 * still carries only the handle, which is the one thing Meta's submission body
 * needs.
 *
 * **3. Meta meters a 24-hour conversation, not a message.** Nothing in this file
 * records a quantity, and nothing derived from it may be rendered as one.
 * Credits are Wend's metering unit; under BYO the tenant's Meta invoice is the
 * tenant's own business and will not agree with a message count. No shape here
 * carries a currency, a "cost" or a "spend".
 *
 * **What is authored and what is only reported.** {@link waTemplateCategorySchema}
 * is authored, so it is a closed list — a fourth value is a submission Meta
 * refuses. Meta's *status* is reported, so it is parsed leniently
 * ({@link parseMetaTemplateStatus} returns `null` for a word we do not know)
 * exactly as `delivery.ts` parses an unknown SES event type: a provider adds
 * vocabulary, and the failure mode of a strict parser is the bad one — the
 * approval never lands and the tenant watches a template sit at `pending`
 * forever.
 *
 * The database half is `migrations/0012_whatsapp_templates.sql`, whose CHECKs
 * hold the discriminants and the counts. This file holds what SQL could not: the
 * field-by-field button shape, contiguity of the positions, and the mapping from
 * a position to a contact field.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { templateStatusSchema, type TemplateStatus } from '../enums.js';
import {
  EXTRAS_VARIABLE_PREFIX,
  parseTemplateVariableName,
  type ParsedTemplateVariable,
} from './template.js';

/* -------------------------------------------------------------------------- */
/* Meta's documented limits — and which of them we actually checked            */
/* -------------------------------------------------------------------------- */

/**
 * The character caps a submission is rejected for exceeding.
 *
 * **Confident** (documented by Meta on the message-templates reference, and the
 * ones a composer hits daily):
 *   - body 1024
 *   - footer 60
 *   - text header 60
 *   - quick-reply button label 25
 *
 * **Guessed, and marked as such rather than presented as fact** — these are the
 * numbers in wide circulation but were not verified against Meta's docs while
 * writing this file, and each is a cap we chose to hold *because being wrong is
 * one edit here, while not holding it is a review cycle and a mark against the
 * WABA's quality rating*:
 *   - URL button label 25 (assumed to match quick-reply)
 *   - button URL 2000
 *   - template name 512
 *
 * `WA_MAX_BUTTONS = 3` is the **narrower** of two true things. Meta permits up
 * to ten buttons on a template once flows, copy-code and phone-number buttons
 * are counted, and 0012's CHECK allows that range so a future button type does
 * not need a migration. Wend authors two types, and three is the documented cap
 * on quick replies; holding the composer to three is a deliberate restriction of
 * what we let a person build, not a claim about Meta's ceiling.
 */
export const WA_BODY_MAX_LENGTH = 1024;
export const WA_FOOTER_MAX_LENGTH = 60;
export const WA_HEADER_TEXT_MAX_LENGTH = 60;
export const WA_BUTTON_LABEL_MAX_LENGTH = 25;
export const WA_BUTTON_URL_MAX_LENGTH = 2000;
export const WA_TEMPLATE_NAME_MAX_LENGTH = 512;
export const WA_MAX_BUTTONS = 3;

/* -------------------------------------------------------------------------- */
/* Category — authored, therefore closed                                       */
/* -------------------------------------------------------------------------- */

/**
 * Meta's three template categories, as they go on the wire (Meta upper-cases
 * them; the column stores them lower-cased, which is what these values are).
 *
 * Mirrored by `templates_wa_category_is_supported` in 0012. Stable since Meta
 * consolidated the old `TRANSACTIONAL`/`OTP` categories in 2023, and the field a
 * rejection most often turns on — a marketing message submitted as `utility` is
 * the classic refusal.
 */
export const WA_TEMPLATE_CATEGORY_VALUES = ['marketing', 'utility', 'authentication'] as const;
export const waTemplateCategorySchema = z.enum(WA_TEMPLATE_CATEGORY_VALUES);
export type WaTemplateCategory = z.infer<typeof waTemplateCategorySchema>;

/** `MARKETING` — how Meta wants it in a Graph request body. */
export function waCategoryToWire(category: WaTemplateCategory): string {
  return category.toUpperCase();
}

/* -------------------------------------------------------------------------- */
/* The header — one optional object, discriminated by format                   */
/* -------------------------------------------------------------------------- */

export const WA_HEADER_FORMAT_VALUES = ['text', 'image', 'document', 'video'] as const;
export const waHeaderFormatSchema = z.enum(WA_HEADER_FORMAT_VALUES);
export type WaHeaderFormat = z.infer<typeof waHeaderFormatSchema>;

/**
 * The header formats that are **a file** rather than characters.
 *
 * Declared here, beside the header it discriminates, and imported by
 * `./whatsapp-media.js` — the arrow points that way and not back, because this
 * file is already in a cycle with `./template.js` and adding a second edge into
 * a module that builds zod schemas at load is how `z.enum(undefined)` happens at
 * import rather than at review.
 *
 * It was an inline `z.enum(['image','document','video'])` on
 * {@link waMediaHeaderSchema} until slice 9 needed the same three words for the
 * per-format limits table. Two literal copies of a provider's vocabulary drift,
 * and the way they drift here is that one of them gains `audio` — which Meta's
 * Cloud API sends but a template header cannot carry — while the other does not.
 *
 * {@link WA_MEDIA_FORMAT_VALUES} is a strict subset of
 * {@link WA_HEADER_FORMAT_VALUES}; the type alias below is checked by the
 * compiler rather than asserted in a comment.
 */
export const WA_MEDIA_FORMAT_VALUES = ['image', 'document', 'video'] as const;
export const waMediaFormatSchema = z.enum(WA_MEDIA_FORMAT_VALUES);
export type WaMediaFormat = z.infer<typeof waMediaFormatSchema>;

/** Fails to compile if a media format is ever added that is not a header format. */
const _waMediaFormatIsAHeaderFormat: readonly WaHeaderFormat[] = WA_MEDIA_FORMAT_VALUES;
void _waMediaFormatIsAHeaderFormat;

/**
 * The header.
 *
 * A discriminated union rather than one object with optional fields, because the
 * two arms have nothing in common: a text header carries characters we validate,
 * a media header carries **a handle Meta issued for bytes Meta already holds**.
 * An object with both fields optional makes "an image header with no handle"
 * representable, and that is a template that looks complete on screen and cannot
 * be submitted. 0012's `templates_wa_header_shape` refuses the same thing from
 * the database side.
 *
 * A text header may contain **at most one** variable, and Meta does not allow
 * one at all in a media header. That rule is enforced by
 * {@link collectWaTemplateVariableIssues}, not by the schema, so the composer can
 * name the offending token instead of failing a `refine` with no location.
 */
export const waTextHeaderSchema = z
  .object({
    format: z.literal('text'),
    text: z.string().trim().min(1).max(WA_HEADER_TEXT_MAX_LENGTH),
  })
  .strict();

export const waMediaHeaderSchema = z
  .object({
    format: waMediaFormatSchema,
    /**
     * What Meta's Resumable Upload returned, for **this submission**. Opaque,
     * and not a URL of ours.
     *
     * SLICE 9 CORRECTED WHAT THIS SENTENCE USED TO CLAIM. It read *"the bytes
     * live in Meta's account, not in `template_assets`"*, which described where
     * Meta keeps its copy and was read as a statement that we keep none. We do:
     * the file sits in `template_assets` under the `wa_header_media` kind, and
     * this string is a **derived, perishable pointer** minted from it — valid
     * for template creation only, and re-minted from our bytes on every
     * resubmission. Left as it was, that sentence argued the next reader into
     * deleting the upload, which is the one thing that makes a rejected template
     * fixable.
     *
     * **This is not a send-time media id.** A send supplies its own media, from
     * a different Meta endpoint, with a different lifetime. `WaReviewHandle` and
     * `WaSendMediaId` in `./whatsapp-media.js` are the two non-interchangeable
     * types that keep them apart; this field is the wire-level flattening of the
     * first, and {@link import('./whatsapp-media.js').waMediaHeaderForReview} is
     * the only supported way to fill it.
     */
    mediaHandle: z.string().trim().min(1).max(512),
    /** Shown in the composer only. Meta never sees it. */
    fileName: z.string().trim().max(255).optional(),
  })
  .strict();
export type WaMediaHeader = z.infer<typeof waMediaHeaderSchema>;

export const waHeaderSchema = z.discriminatedUnion('format', [waTextHeaderSchema, waMediaHeaderSchema]);
export type WaHeader = z.infer<typeof waHeaderSchema>;

/* -------------------------------------------------------------------------- */
/* Buttons                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The two button types Wend authors.
 *
 * Not every type Meta supports (phone number, copy code, flow, one-tap OTP), and
 * that asymmetry is on purpose in both directions: 0012 leaves the *stored*
 * types open so a third type is a change to this file alone, while this schema
 * is closed so the composer cannot produce a button the send path has no branch
 * for.
 */
export const WA_BUTTON_TYPE_VALUES = ['quick_reply', 'url'] as const;
export const waButtonTypeSchema = z.enum(WA_BUTTON_TYPE_VALUES);
export type WaButtonType = z.infer<typeof waButtonTypeSchema>;

export const waQuickReplyButtonSchema = z
  .object({
    type: z.literal('quick_reply'),
    text: z.string().trim().min(1).max(WA_BUTTON_LABEL_MAX_LENGTH),
  })
  .strict();

/**
 * A URL button.
 *
 * `https:` only, and that is the same rule slice 4's link schema applies to an
 * email: `http:` is a link we would be asking a bank's customer to follow in
 * clear, and Meta rejects most other schemes anyway.
 *
 * The URL may end in **one** `{{1}}`-style suffix variable (Meta's "dynamic
 * URL"). It is deliberately *not* modelled as a free variable position here —
 * see {@link waUrlButtonSuffixVariable}.
 */
export const waUrlButtonSchema = z
  .object({
    type: z.literal('url'),
    text: z.string().trim().min(1).max(WA_BUTTON_LABEL_MAX_LENGTH),
    url: z
      .string()
      .trim()
      .min(1)
      .max(WA_BUTTON_URL_MAX_LENGTH)
      .refine((value) => value.toLowerCase().startsWith('https://'), 'A button URL must be https://'),
  })
  .strict();

export const waButtonSchema = z.discriminatedUnion('type', [waQuickReplyButtonSchema, waUrlButtonSchema]);
export type WaButton = z.infer<typeof waButtonSchema>;

/**
 * The trailing variable of a dynamic URL button, if it has one.
 *
 * Meta allows exactly one, and only as the **suffix** of the URL. Returning the
 * position rather than a boolean lets the composer bind it like any other
 * position; returning `null` for a variable anywhere but at the end is what
 * makes `https://x.example/{{1}}/orders` an error the composer can name instead
 * of a Graph rejection.
 */
export function waUrlButtonSuffixVariable(url: string): number | null {
  const match = /\{\{(\d+)\}\}$/.exec(url.trim());
  if (!match?.[1]) return null;
  const position = Number(match[1]);
  return Number.isSafeInteger(position) && position >= 1 ? position : null;
}

/* -------------------------------------------------------------------------- */
/* Positional variables — the parser that is deliberately NOT the email one    */
/* -------------------------------------------------------------------------- */

/**
 * The token grammar: `{{1}}`.
 *
 * The capture is `[^{}]*` — everything between the braces — for the same reason
 * slice 4's is: capture first, *validate* second, so a mistake becomes a named
 * error at a known offset instead of a silent non-match. A regex that only
 * matched `\{\{\d+\}\}` would make `{{ name }}` invisible to the validator, and
 * invisible is precisely how the email grammar reaches Meta.
 *
 * **Why this is not `templateVariablePattern()`.** The two patterns are
 * identical today and must not be shared, because they are validated against
 * different grammars and only one of them may loosen. The email pattern exists
 * to accept spreadsheet headers (`{{ extras.Order Total }}`); this one exists to
 * accept nothing but digits. A single shared function is one refactor away from
 * a WhatsApp body containing `{{ name }}` and a WABA collecting rejections.
 */
export const WA_VARIABLE_PATTERN_SOURCE = '\\{\\{([^{}]*)\\}\\}';

/**
 * A **fresh** global matcher every call — a module-level `/g` regex carries
 * `lastIndex` between calls, so two templates validated in one request would
 * give different answers. Same reasoning as slice 4.
 */
export function waVariablePattern(): RegExp {
  return new RegExp(WA_VARIABLE_PATTERN_SOURCE, 'g');
}

/** `{{1}}` — the canonical rendering. No spaces: Meta's own examples have none. */
export function waVariableToken(position: number): string {
  return `{{${position}}}`;
}

/** The largest position we accept. Meta has no documented cap; this one is ours. */
export const WA_MAX_VARIABLE_POSITION = 99;

export const WA_VARIABLE_ISSUE_REASON_VALUES = [
  /** `{{ name }}`, `{{extras.x}}` — email's grammar. Meta rejects it. */
  'not_positional',
  /** `{{0}}`. Meta counts from 1, and the off-by-one is only visible in the rejection. */
  'zero_position',
  /** `{{ 1 }}` — Meta's parser is literal about the braces. */
  'has_whitespace',
  /** `{{01}}` — parses as 1 and renders as neither. Refused rather than guessed at. */
  'leading_zero',
  /** Above {@link WA_MAX_VARIABLE_POSITION}, or not a safe integer. */
  'position_out_of_range',
  /** `{{}}`. */
  'empty',
] as const;
export const waVariableIssueReasonSchema = z.enum(WA_VARIABLE_ISSUE_REASON_VALUES);
export type WaVariableIssueReason = z.infer<typeof waVariableIssueReasonSchema>;

export type ParsedWaVariable =
  | { readonly ok: true; readonly position: number }
  | { readonly ok: false; readonly reason: WaVariableIssueReason };

/**
 * Validates one token's contents — everything between the braces, as written.
 *
 * Whitespace is rejected rather than trimmed. That is the one place this parser
 * is *stricter than it could be*, and it is deliberate: `{{ 1 }}` is a thing a
 * person coming from the email composer types, Meta does not substitute it, and
 * the message that reaches a real customer's phone then contains a literal
 * `{{ 1 }}`. Silently trimming would hide a habit that produces broken messages
 * everywhere else the person types it.
 */
export function parseWaVariableToken(raw: string): ParsedWaVariable {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, reason: 'empty' };

  // Digits first, whitespace second, and the order is the point: `{{ name }}` is
  // both non-positional and padded, and naming it `has_whitespace` would tell a
  // person to delete two spaces when the real problem is that they wrote a name.
  if (!/^\d+$/.test(trimmed)) return { ok: false, reason: 'not_positional' };
  if (trimmed !== raw) return { ok: false, reason: 'has_whitespace' };
  if (trimmed === '0') return { ok: false, reason: 'zero_position' };
  if (trimmed.startsWith('0')) return { ok: false, reason: 'leading_zero' };

  const position = Number(trimmed);
  if (!Number.isSafeInteger(position) || position > WA_MAX_VARIABLE_POSITION) {
    return { ok: false, reason: 'position_out_of_range' };
  }
  return { ok: true, position };
}

/** Where in a WhatsApp template a token was written. Used to name the offender. */
export type WaVariableLocation =
  | { readonly kind: 'header' }
  | { readonly kind: 'body' }
  | { readonly kind: 'footer' }
  | { readonly kind: 'button'; readonly index: number };

export interface WaVariableOccurrence {
  /** Exactly as written between the braces. Not trimmed — see the parser. */
  readonly raw: string;
  /** As it appeared in the source, braces included. */
  readonly token: string;
  readonly location: WaVariableLocation;
}

export interface WaVariableIssue extends WaVariableOccurrence {
  readonly reason: WaVariableIssueReason;
  /** Names the offending token and where it is. Safe to show a customer. */
  readonly message: string;
}

/** Every `{{ … }}` in one string, in order, valid or not. */
export function extractWaVariables(text: string, location: WaVariableLocation): WaVariableOccurrence[] {
  const found: WaVariableOccurrence[] = [];
  const pattern = waVariablePattern();
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    found.push({ raw: match[1] ?? '', token: match[0], location });
  }
  return found;
}

function describeLocation(location: WaVariableLocation): string {
  switch (location.kind) {
    case 'header':
      return 'the header';
    case 'body':
      return 'the body';
    case 'footer':
      return 'the footer';
    case 'button':
      return `button ${location.index + 1}`;
  }
}

const WA_VARIABLE_ISSUE_MESSAGE: Readonly<Record<WaVariableIssueReason, string>> = {
  not_positional: 'WhatsApp variables are numbered — write {{1}}, not a name',
  zero_position: 'WhatsApp numbers variables from {{1}} — there is no {{0}}',
  has_whitespace: 'WhatsApp variables carry no spaces — write {{1}}, not {{ 1 }}',
  leading_zero: 'Write {{1}} rather than {{01}}',
  position_out_of_range: `A variable position must be between 1 and ${WA_MAX_VARIABLE_POSITION}`,
  empty: 'Empty {{ }} — a WhatsApp variable needs a number',
};

/* -------------------------------------------------------------------------- */
/* Contiguity — the rule SQL could not express                                 */
/* -------------------------------------------------------------------------- */

/**
 * What a set of positions is wrong about, as a whole rather than one token at a
 * time.
 *
 * `gap` is the one that matters and the one no CHECK could see: a body using
 * `{{1}}` and `{{3}}` is refused by Meta, with a message that describes the
 * template rather than the missing number. Repeats are **not** an error — the
 * same position may legitimately appear twice ("Hi {{1}}, your {{2}} is ready,
 * {{1}}") and Meta substitutes both.
 */
export const WA_POSITION_SET_ISSUE_VALUES = [
  /** Positions are not contiguous from 1 — e.g. 1 and 3, or 2 alone. */
  'gap',
] as const;
export const waPositionSetIssueSchema = z.enum(WA_POSITION_SET_ISSUE_VALUES);
export type WaPositionSetIssue = z.infer<typeof waPositionSetIssueSchema>;

export interface WaPositionSet {
  /** Distinct positions, ascending. Empty for a template with no variables. */
  readonly positions: readonly number[];
  /** The positions 1..max that were never written. Empty when contiguous. */
  readonly missing: readonly number[];
  readonly contiguous: boolean;
}

/**
 * Collapses parsed positions into the set, and says which numbers are missing.
 *
 * A template with **no** variables is contiguous and valid — most utility
 * templates have none, and returning an issue for the empty set would refuse
 * them. That is a real case, not a degenerate one.
 */
export function summariseWaPositions(positions: readonly number[]): WaPositionSet {
  const distinct = [...new Set(positions)].sort((a, b) => a - b);
  if (distinct.length === 0) return { positions: [], missing: [], contiguous: true };

  const highest = distinct[distinct.length - 1] ?? 0;
  const present = new Set(distinct);
  const missing: number[] = [];
  for (let n = 1; n <= highest; n += 1) if (!present.has(n)) missing.push(n);

  return { positions: distinct, missing, contiguous: missing.length === 0 };
}

/* -------------------------------------------------------------------------- */
/* Position → contact field                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A variable position, as one shape reused everywhere a position is carried.
 *
 * Extracted in slice 49 rather than written out three more times: the binding,
 * the campaign read's `requiredPositions`, what {@link waBindingState} derives
 * from it and the preview's per-position rows all mean the same number, and the range
 * message is the one {@link WA_VARIABLE_ISSUE_MESSAGE} already prints for
 * `position_out_of_range` so one product says one thing about `{{0}}` and
 * `{{100}}` whether they were typed into a template or into a binding.
 */
export const waPositionSchema = z
  .number()
  .int()
  .min(1, WA_VARIABLE_ISSUE_MESSAGE.position_out_of_range)
  .max(WA_MAX_VARIABLE_POSITION, WA_VARIABLE_ISSUE_MESSAGE.position_out_of_range);

/**
 * What one position is filled from at send.
 *
 * The **target** grammar is slice 4's, reused rather than re-declared:
 * `parseTemplateVariableName` decides whether `extras.Order Total` is a thing a
 * contact can supply, and it must give the same answer for both channels or
 * `extras.` means two different things in one product. Only the *token* grammar
 * differs, which is the whole distinction this file is built around.
 *
 * **`fallback` IS REQUIRED AND MAY NOT BE EMPTY, and slice 49 tightened it from
 * the opposite.** It used to be `z.string().max(200).default('')`, described as
 * *"substituted when the contact's field is null or empty"* — which read as a
 * nicety and defaulted to precisely the value Meta refuses. The chain is short
 * and it ends at a real phone: `waValuesForRecipient` substitutes the fallback
 * exactly when the contact's field is empty, `assembleWhatsAppMessage` throws on
 * an empty parameter because Meta rejects one, and the runner turns that into a
 * `failed` recipient. So an optional fallback converts "this contact has no
 * first name" into a send that dies a third of the way through, on the tenant's
 * own WABA. Required, it costs one word typed by the person composing the
 * campaign, at the moment they are choosing the field. The old default is left
 * described here rather than deleted because a reader who finds only
 * `.min(1)` will otherwise make it optional again to be kind.
 */
export const waVariableBindingSchema = z
  .object({
    position: waPositionSchema,
    /** `name`, `email`, `phoneE164`, or `extras.<column>` — slice 4's grammar. */
    source: z.string().trim().min(1),
    /** REQUIRED and non-empty. See above: Meta refuses an empty parameter. */
    fallback: z
      .string()
      .trim()
      .min(
        1,
        'Fill in “If empty”. WhatsApp refuses a message with an empty parameter, so anyone whose column is blank would get no message at all rather than one with a gap in it.',
      )
      .max(200),
  })
  .strict();
export type WaVariableBinding = z.infer<typeof waVariableBindingSchema>;

/**
 * The one array shape a create, an update and a preview all bind through.
 *
 * It carries the two refusals that need **nothing but the request itself**, so
 * they cannot be skipped by a caller that forgot to ask a service: a position
 * bound twice, and a source Wend has no field for. Both land on a positional
 * path (`[index, 'position']`) because the composer draws one row per binding
 * and numbers them from 1 — a message with no row on it is a message a person
 * cannot act on, which is the whole of the refusal rule.
 *
 * What it deliberately does **not** check is whether these positions are the
 * ones the template needs. That needs the `templates` row, so it is
 * `campaignScheduleIssues`' job and it is re-derived inside the scheduling
 * transaction: a template edited under a draft can grow `{{3}}` after this
 * schema last saw the body.
 */
export const waBindingListSchema = z
  .array(waVariableBindingSchema)
  .max(WA_MAX_VARIABLE_POSITION)
  .superRefine((bindings, ctx) => {
    const seen = new Map<number, number>();
    bindings.forEach((binding, index) => {
      const first = seen.get(binding.position);
      if (first === undefined) {
        seen.set(binding.position, index);
      } else {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'position'],
          message: `${waVariableToken(binding.position)} already has a field. One position takes one field.`,
        });
      }
      if (!parseWaBindingSource(binding.source).ok) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'source'],
          message: `Wend has no field called “${binding.source}”. Choose one of the contact fields in the list, or pick a column from your upload.`,
        });
      }
    });
  });
export type WaBindingList = z.infer<typeof waBindingListSchema>;

/**
 * Which positions a template needs and nothing fills, and which fields it no
 * longer uses.
 *
 * ONE derivation, exported, because two callers ask the same question: the
 * schedule gate and the personalisation panel's rows.
 * Slice 49's review found the browser and the server each answering it
 * separately, which is this repository's worst defect shape — both sides go on
 * rendering something, and the drift is invisible until the two disagree on a
 * screen. The campaign read answers it no longer: it carries
 * `requiredPositions` and lets both sides call this.
 *
 * Neither list is deduplicated, because neither can repeat: a document's
 * positions are a set, and `waBindingListSchema` refuses a position twice.
 */
export function waBindingState(
  requiredPositions: readonly number[],
  bindings: readonly WaVariableBinding[],
): { readonly unbound: readonly number[]; readonly unused: readonly number[] } {
  const bound = new Set(bindings.map((binding) => binding.position));
  const required = new Set(requiredPositions);

  return {
    unbound: requiredPositions.filter((position) => !bound.has(position)),
    unused: bindings.map((binding) => binding.position).filter((position) => !required.has(position)),
  };
}

/** Resolves a binding's `source` through the email contract's target grammar. */
export function parseWaBindingSource(source: string): ParsedTemplateVariable {
  return parseTemplateVariableName(source);
}

/** `extras.` — re-exported so a WhatsApp consumer need not import from `template.js`. */
export { EXTRAS_VARIABLE_PREFIX };

/* -------------------------------------------------------------------------- */
/* The document                                                                */
/* -------------------------------------------------------------------------- */

/**
 * A WhatsApp template as Wend holds it, mapping 1:1 onto 0012's `wa_*` columns.
 *
 * `bindings` has no column of its own: `templates.variables` holds the positional
 * tokens for both channels, and the binding list travels with the campaign that
 * chose an audience. That is the same split slice 4 made — the template declares
 * what it *needs*, the campaign supplies it.
 */
export const waTemplateDocumentSchema = z
  .object({
    category: waTemplateCategorySchema,
    /** Meta requires a language on every template. BCP-47-ish, e.g. `en_US`. */
    language: z.string().trim().min(2).max(15).default('en'),
    header: waHeaderSchema.nullable().default(null),
    /** THE MESSAGE. Plain text; WhatsApp has no HTML. */
    body: z.string().trim().min(1).max(WA_BODY_MAX_LENGTH),
    footer: z.string().trim().min(1).max(WA_FOOTER_MAX_LENGTH).nullable().default(null),
    buttons: z.array(waButtonSchema).max(WA_MAX_BUTTONS).default([]),
  })
  .strict();
export type WaTemplateDocument = z.infer<typeof waTemplateDocumentSchema>;

/**
 * Every token in a document, in submission order.
 *
 * Order is header → body → buttons. The footer is scanned too, and any token
 * found there is an issue rather than a variable: **Meta allows no variables in
 * a footer**, and a footer reading `{{1}}` reaches the phone as those five
 * literal characters.
 */
export function extractWaDocumentVariables(document: WaTemplateDocument): WaVariableOccurrence[] {
  const found: WaVariableOccurrence[] = [];
  if (document.header?.format === 'text') {
    found.push(...extractWaVariables(document.header.text, { kind: 'header' }));
  }
  found.push(...extractWaVariables(document.body, { kind: 'body' }));
  if (document.footer !== null) found.push(...extractWaVariables(document.footer, { kind: 'footer' }));
  document.buttons.forEach((button, index) => {
    if (button.type === 'url') found.push(...extractWaVariables(button.url, { kind: 'button', index }));
  });
  return found;
}

/**
 * **THE** derivation of which positions a template actually needs a value for.
 *
 * Header (text only) → body → URL buttons, ascending and deduplicated. The
 * footer is excluded because Meta allows no variable there at all — a `{{1}}` in
 * a footer reaches the phone as five literal characters, and
 * {@link collectWaTemplateVariableIssues} already refuses it as an issue rather
 * than counting it as a need.
 *
 * It lives here, in the contract, because four places ask this question — the
 * campaign detail read, the schedule gate, the send-time lock and the
 * personalisation panel — and the failure mode of four implementations is not
 * that they all break. It is that one of them reads the body and forgets the
 * header or a dynamic URL button, so the panel shows every position bound, the
 * gate allows the schedule, and Meta refuses the send one recipient at a time.
 * `requiredWaPositions(source)` in the API's `whatsapp-message-source.ts`
 * delegates to this rather than walking its own per-component maps.
 *
 * Parsed with {@link parseWaVariableToken} and never slice 4's named parser: a
 * `{{ name }}` in a WhatsApp body is a token Meta refuses, not a position.
 */
export function requiredWaPositionsForDocument(document: WaTemplateDocument): number[] {
  const positions = new Set<number>();
  for (const occurrence of extractWaDocumentVariables(document)) {
    if (occurrence.location.kind === 'footer') continue;
    const parsed = parseWaVariableToken(occurrence.raw);
    if (parsed.ok) positions.add(parsed.position);
  }
  return [...positions].sort((a, b) => a - b);
}

/**
 * One printer for a list of positions: `{{1}}`, `{{1}} and `{{3}}`,
 * `{{1}}, {{2}} and {{3}}`.
 *
 * Shared by the schedule blocker and the send-time refusal — the two places a
 * list of positions is printed — for
 * the reason `campaignSkipReasonSentence` is shared: three renderings of one
 * list is three ways to read the same sentence, and the one a person compares
 * against their template is whichever they happen to be looking at. Empty in,
 * empty out — a caller with nothing to name should not be printing a sentence,
 * and returning `''` makes that visible rather than producing "needs , and .".
 */
export function formatWaPositions(positions: readonly number[]): string {
  const tokens = positions.map((position) => waVariableToken(position));
  if (tokens.length === 0) return '';
  if (tokens.length === 1) return tokens[0] ?? '';
  return `${tokens.slice(0, -1).join(', ')} and ${tokens[tokens.length - 1] ?? ''}`;
}

/** A document-level rule broken, named where the composer can point at it. */
export interface WaDocumentIssue {
  readonly code: 'gap' | 'footer_variable' | 'header_variable_count' | 'media_header_variable';
  readonly message: string;
}

/**
 * Every reason this document would be rejected, decided in Wend rather than by
 * Meta.
 *
 * This function is the whole of "do not submit something we already know will be
 * refused". It returns both per-token issues and document-level ones, because a
 * composer that showed one and then the other would put a person through two
 * round-trips for one submission.
 */
export function collectWaTemplateVariableIssues(document: WaTemplateDocument): {
  readonly tokenIssues: readonly WaVariableIssue[];
  readonly documentIssues: readonly WaDocumentIssue[];
  readonly positions: WaPositionSet;
} {
  const occurrences = extractWaDocumentVariables(document);
  const tokenIssues: WaVariableIssue[] = [];
  const positions: number[] = [];
  let headerVariables = 0;

  for (const occurrence of occurrences) {
    const parsed = parseWaVariableToken(occurrence.raw);
    if (!parsed.ok) {
      tokenIssues.push({
        ...occurrence,
        reason: parsed.reason,
        message: `${WA_VARIABLE_ISSUE_MESSAGE[parsed.reason]} (${occurrence.token} in ${describeLocation(occurrence.location)})`,
      });
      continue;
    }
    if (occurrence.location.kind === 'header') headerVariables += 1;
    positions.push(parsed.position);
  }

  const documentIssues: WaDocumentIssue[] = [];
  const summary = summariseWaPositions(positions);
  if (!summary.contiguous) {
    documentIssues.push({
      code: 'gap',
      message: `WhatsApp variables must run from {{1}} with no gaps — ${summary.missing
        .map((n) => waVariableToken(n))
        .join(', ')} ${summary.missing.length === 1 ? 'is' : 'are'} missing`,
    });
  }
  if (occurrences.some((o) => o.location.kind === 'footer')) {
    documentIssues.push({
      code: 'footer_variable',
      message: 'A WhatsApp footer cannot contain a variable',
    });
  }
  if (headerVariables > 1) {
    documentIssues.push({
      code: 'header_variable_count',
      message: 'A WhatsApp header may contain at most one variable',
    });
  }

  return { tokenIssues, documentIssues, positions: summary };
}

/** True when nothing in the document is known-refusable. The submit gate. */
export function isWaTemplateSubmittable(document: WaTemplateDocument): boolean {
  const issues = collectWaTemplateVariableIssues(document);
  return issues.tokenIssues.length === 0 && issues.documentIssues.length === 0;
}

/* -------------------------------------------------------------------------- */
/* Meta's status vocabulary — reported, therefore parsed leniently             */
/* -------------------------------------------------------------------------- */

/**
 * Folds a provider's spelling down to something matchable — the same one-line
 * normalisation `delivery.ts` applies to SES event types, and for the same
 * reason: Meta writes `PENDING_DELETION` in one place and `Pending deletion` in
 * another, and an exact-match table would silently recognise neither.
 */
function foldWireValue(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Every template status Meta is documented to report, lower-cased. Stored
 * verbatim in `templates.meta_status`, which 0012 leaves an **open** shape check
 * precisely so a word not in this list still lands.
 */
export const META_TEMPLATE_STATUS_VALUES = [
  'approved',
  'pending',
  'rejected',
  'paused',
  'disabled',
  'in_appeal',
  'pending_deletion',
  'deleted',
  'limit_exceeded',
] as const;
export const metaTemplateStatusSchema = z.enum(META_TEMPLATE_STATUS_VALUES);
export type MetaTemplateStatus = z.infer<typeof metaTemplateStatusSchema>;

const META_STATUS_BY_WIRE_VALUE: Readonly<Record<string, MetaTemplateStatus>> = {
  APPROVED: 'approved',
  PENDING: 'pending',
  REJECTED: 'rejected',
  PAUSED: 'paused',
  DISABLED: 'disabled',
  IN_APPEAL: 'in_appeal',
  PENDING_DELETION: 'pending_deletion',
  DELETED: 'deleted',
  LIMIT_EXCEEDED: 'limit_exceeded',
};

const FOLDED_META_STATUS = new Map<string, MetaTemplateStatus>(
  Object.entries(META_STATUS_BY_WIRE_VALUE).map(([wire, member]) => [foldWireValue(wire), member]),
);

/**
 * Meta's word → ours, or `null` for a word we do not know.
 *
 * **Returns `null` rather than throwing**, exactly as `parseSesEventType` does.
 * Meta has added statuses to this webhook before (`IN_APPEAL` and
 * `PENDING_DELETION` both arrived after the field shipped), and a parser that
 * threw would turn a new status into a 500 and a webhook Meta eventually stops
 * calling. The caller stores the raw value and acts on nothing.
 */
export function parseMetaTemplateStatus(raw: unknown): MetaTemplateStatus | null {
  return typeof raw === 'string' ? (FOLDED_META_STATUS.get(foldWireValue(raw)) ?? null) : null;
}

/**
 * Meta's status → Wend's fixed four.
 *
 * Two decisions live in this table and neither is obvious.
 *
 * **`paused` and `disabled` fold to `rejected`.** Neither is a rejection; both
 * mean "Meta approved this and has now stopped it", and `template_status` has no
 * member for that. Folding them **towards unsendable** is the only safe
 * direction: the alternative leaves a template `approved` that Meta will refuse
 * at send, which surfaces as a campaign failing mid-flight rather than a
 * template a human is asked to look at. `templates.meta_status` still holds the
 * true word, which is why 0012 keeps it and refuses to make the two columns
 * agree.
 *
 * **`pending_deletion` and `in_appeal` fold to `pending`.** Both are "Meta has
 * not finished deciding", which is what our `pending` means.
 */
export const TEMPLATE_STATUS_BY_META_STATUS: Readonly<Record<MetaTemplateStatus, TemplateStatus>> = {
  approved: 'approved',
  pending: 'pending',
  in_appeal: 'pending',
  pending_deletion: 'pending',
  rejected: 'rejected',
  paused: 'rejected',
  disabled: 'rejected',
  deleted: 'rejected',
  limit_exceeded: 'rejected',
};

/**
 * The whole "what does this status mean for us" decision, in one call.
 *
 * `null` in, `null` out: an unrecognised status changes nothing about
 * `templates.status`. One function rather than a `switch` in the webhook and
 * another on the templates screen — that is how the ledger and the screen come
 * to disagree, and `delivery.ts` says the same thing one file over.
 */
export function templateStatusFromMeta(raw: unknown): {
  readonly metaStatus: MetaTemplateStatus | null;
  readonly status: TemplateStatus | null;
  readonly sendable: boolean;
} {
  const metaStatus = parseMetaTemplateStatus(raw);
  if (metaStatus === null) return { metaStatus: null, status: null, sendable: false };
  const status = TEMPLATE_STATUS_BY_META_STATUS[metaStatus];
  return { metaStatus, status, sendable: status === 'approved' };
}

/** The only status a WhatsApp campaign may send from. Fails closed on `null`. */
export function isWaTemplateSendable(status: TemplateStatus | null | undefined): boolean {
  return status === 'approved';
}

/* -------------------------------------------------------------------------- */
/* The Meta webhook                                                            */
/* -------------------------------------------------------------------------- */

/**
 * `X-Hub-Signature-256` — the header name, spelled once.
 *
 * Meta sends `sha256=<hex digest>`, an HMAC-SHA256 over the **raw request
 * bytes** keyed with the app secret. Over the raw bytes, not over
 * `JSON.stringify(req.body)`: re-serialising changes key order, whitespace and
 * unicode escaping, and the digest then never matches for reasons that look like
 * a wrong secret. {@link MetaWebhookDelivery} carries `rawBody` as a
 * `Uint8Array` so the module cannot accidentally be handed a parsed object —
 * this package has no Node types, and the API's Buffer is one.
 */
export const META_SIGNATURE_HEADER = 'x-hub-signature-256';
export const META_SIGNATURE_PREFIX = 'sha256=';

/**
 * The hex digest out of the header, or `null`.
 *
 * A digest of the wrong length is `null` rather than a shorter comparison: a
 * constant-time compare against a truncated digest is a compare that succeeds on
 * a prefix, which is the classic way this check is defeated.
 */
export function parseMetaSignatureHeader(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value.toLowerCase().startsWith(META_SIGNATURE_PREFIX)) return null;
  const digest = value.slice(META_SIGNATURE_PREFIX.length).trim().toLowerCase();
  return /^[0-9a-f]{64}$/.test(digest) ? digest : null;
}

/**
 * What the endpoint hands the verifier: the bytes as they arrived, and the
 * header as it arrived. Nothing parsed.
 *
 * **Verify first, parse second, act third** — slice 6's rule, unchanged. The
 * body is stored in `webhook_events` before it is understood, so an event we
 * could not parse still exists.
 */
export interface MetaWebhookDelivery {
  readonly rawBody: Uint8Array;
  readonly signatureHeader: string | null;
}

export const META_WEBHOOK_VERDICT_VALUES = [
  'valid',
  'invalid_signature',
  /** No `X-Hub-Signature-256`, or one that was not `sha256=<64 hex>`. */
  'missing_signature',
  /** No app secret configured — **fails closed**, nothing is acted on. */
  'unconfigured',
  /** The bytes were not a Meta webhook envelope at all. */
  'malformed',
] as const;
export const metaWebhookVerdictSchema = z.enum(META_WEBHOOK_VERDICT_VALUES);
export type MetaWebhookVerdict = z.infer<typeof metaWebhookVerdictSchema>;

/**
 * **The only verdict that may change a template's status.**
 *
 * A function, not a comparison at the call site, for the reason
 * `mayActOnEvent` gives one file over: a caller writing
 * `verdict !== 'invalid_signature'` is true for three other failures, one of
 * which is "we have no secret to check against".
 */
export function mayActOnMetaWebhook(verdict: MetaWebhookVerdict): boolean {
  return verdict === 'valid';
}

/**
 * Meta's GET verification handshake, sent once when the callback URL is saved.
 *
 * The dotted names are Meta's and are not renamed: this schema parses a query
 * string we do not control.
 */
export const metaWebhookVerificationQuerySchema = z
  .object({
    'hub.mode': z.string().optional(),
    'hub.challenge': z.string().optional(),
    'hub.verify_token': z.string().optional(),
  })
  .passthrough();
export type MetaWebhookVerificationQuery = z.infer<typeof metaWebhookVerificationQuerySchema>;

/**
 * The status-update payload.
 *
 * `.passthrough()` and almost entirely optional, throughout — the same posture
 * `snsEnvelopeSchema` takes. This schema's job is to get us from verified bytes
 * to a template id and a status; it is not a second, weaker gate in front of the
 * signature, and refusing a field Meta added would refuse a real approval.
 *
 * The event lives at `entry[].changes[].value.event`, and the template Meta is
 * talking about at `.message_template_id` — which is `templates.meta_template_id`,
 * matched on rather than trusted as a row id.
 */
export const metaTemplateStatusChangeValueSchema = z
  .object({
    /** `APPROVED` | `REJECTED` | … — Meta's word. Parsed by {@link parseMetaTemplateStatus}. */
    event: z.string().optional(),
    message_template_id: z.union([z.string(), z.number()]).optional(),
    message_template_name: z.string().optional(),
    message_template_language: z.string().optional(),
    /** Present on a rejection, and **documented to be absent sometimes**. */
    reason: z.string().nullable().optional(),
    /** Meta's word for why an approved template was paused or disabled. */
    disable_info: z.object({ disable_date: z.string().optional() }).passthrough().optional(),
    other_info: z.object({ title: z.string().optional() }).passthrough().optional(),
  })
  .passthrough();
export type MetaTemplateStatusChangeValue = z.infer<typeof metaTemplateStatusChangeValueSchema>;

/** The field name we act on. Meta sends several others down the same callback. */
export const META_TEMPLATE_STATUS_FIELD = 'message_template_status_update';

export const metaWebhookChangeSchema = z
  .object({
    field: z.string().optional(),
    value: z.unknown().optional(),
  })
  .passthrough();

export const metaWebhookPayloadSchema = z
  .object({
    object: z.string().optional(),
    entry: z
      .array(
        z
          .object({
            /** The WABA id. Matched against the tenant's integration config. */
            id: z.union([z.string(), z.number()]).optional(),
            time: z.number().optional(),
            changes: z.array(metaWebhookChangeSchema).optional(),
          })
          .passthrough(),
      )
      .optional(),
  })
  .passthrough();
export type MetaWebhookPayload = z.infer<typeof metaWebhookPayloadSchema>;

/** One status update, flattened out of the envelope. */
export interface MetaTemplateStatusUpdate {
  readonly wabaId: string | null;
  readonly metaTemplateId: string | null;
  readonly templateName: string | null;
  readonly metaStatus: MetaTemplateStatus | null;
  readonly status: TemplateStatus | null;
  /** Meta's rejection reason, verbatim. Null when Meta omitted it. */
  readonly reason: string | null;
}

/**
 * Every status update in one payload.
 *
 * Meta batches: one POST can carry several entries, each with several changes,
 * and a reader that took `entry[0].changes[0]` would drop approvals silently.
 * Changes whose `field` is not {@link META_TEMPLATE_STATUS_FIELD} are ignored
 * here and still stored by the caller.
 */
export function extractMetaTemplateStatusUpdates(payload: MetaWebhookPayload): MetaTemplateStatusUpdate[] {
  const updates: MetaTemplateStatusUpdate[] = [];
  for (const entry of payload.entry ?? []) {
    const wabaId = entry.id === undefined ? null : String(entry.id);
    for (const change of entry.changes ?? []) {
      if (change.field !== META_TEMPLATE_STATUS_FIELD) continue;
      const parsed = metaTemplateStatusChangeValueSchema.safeParse(change.value);
      if (!parsed.success) continue;
      const value = parsed.data;
      const decided = templateStatusFromMeta(value.event);
      updates.push({
        wabaId,
        metaTemplateId: value.message_template_id === undefined ? null : String(value.message_template_id),
        templateName: value.message_template_name ?? null,
        metaStatus: decided.metaStatus,
        status: decided.status,
        reason: value.reason ?? null,
      });
    }
  }
  return updates;
}

/** What the WhatsApp webhook answers. Says nothing about what matched — see slice 6. */
export const metaWebhookIngestResultSchema = z
  .object({
    received: z.literal(true),
    verdict: metaWebhookVerdictSchema,
    stored: z.boolean(),
    processed: z.boolean(),
  })
  .strict();
export type MetaWebhookIngestResult = z.infer<typeof metaWebhookIngestResultSchema>;

/* -------------------------------------------------------------------------- */
/* Rejection, and the fix a human must approve                                 */
/* -------------------------------------------------------------------------- */

/**
 * Meta's rejection reasons, folded. Reported, so unknown parses to `null` and
 * the raw text is what the human reads.
 */
export const META_REJECTION_REASON_VALUES = [
  'abusive_content',
  'incorrect_category',
  'invalid_format',
  'scam',
  'promotional',
  'tag_content_mismatch',
  'none',
] as const;
export const metaRejectionReasonSchema = z.enum(META_REJECTION_REASON_VALUES);
export type MetaRejectionReason = z.infer<typeof metaRejectionReasonSchema>;

const FOLDED_REJECTION_REASON = new Map<string, MetaRejectionReason>(
  META_REJECTION_REASON_VALUES.map((member) => [foldWireValue(member), member]),
);

/** Meta's reason → ours, `null` for anything else. Never throws. */
export function parseMetaRejectionReason(raw: unknown): MetaRejectionReason | null {
  return typeof raw === 'string' ? (FOLDED_REJECTION_REASON.get(foldWireValue(raw)) ?? null) : null;
}

/**
 * What we suggest doing about a rejection.
 *
 * `recategorise` is the only fix that is mechanically derivable — Meta's
 * `incorrect_category` says exactly what it wants — and even that is a
 * *proposal*. Everything else is `edit_content`, which means "a person must
 * read Meta's reason and rewrite something".
 */
export const WA_PROPOSED_FIX_KIND_VALUES = ['recategorise', 'edit_content', 'no_proposal'] as const;
export const waProposedFixKindSchema = z.enum(WA_PROPOSED_FIX_KIND_VALUES);
export type WaProposedFixKind = z.infer<typeof waProposedFixKindSchema>;

/**
 * A rejection, and the fix waiting for a human.
 *
 * **`approvedBy` is null until a person approves it, and nothing resubmits
 * without one.** SLICES: *no automatic resubmit — a rejection loop against Meta
 * is how a WABA gets flagged.* The shape carries the approval rather than
 * leaving it to a service to remember, so "who decided to send this back to
 * Meta" is a column and not an inference from a log.
 *
 * There is no `resubmitAt`, no retry count and no backoff anywhere in this file.
 * Those are the shapes of a thing that resubmits itself.
 */
export const waTemplateRejectionSchema = z
  .object({
    templateId: uuidSchema,
    /** Meta's word, verbatim and lower-cased. `templates.meta_rejection_reason`. */
    reason: z.string().min(1),
    /** The same, folded to our vocabulary. Null when Meta said something new. */
    reasonCode: metaRejectionReasonSchema.nullable(),
    rejectedAt: isoDateTimeSchema,
    proposedFix: z
      .object({
        kind: waProposedFixKindSchema,
        /** Shown to the human. Plain language, never presented as certain. */
        summary: z.string().min(1),
        /** Set only for `recategorise`. The category we would submit instead. */
        category: waTemplateCategorySchema.nullable().default(null),
      })
      .strict(),
    /** Null until a person approves the fix. A resubmission requires it. */
    approvedBy: uuidSchema.nullable().default(null),
    approvedAt: isoDateTimeSchema.nullable().default(null),
  })
  .strict();
export type WaTemplateRejection = z.infer<typeof waTemplateRejectionSchema>;

/**
 * Proposes a fix from what Meta said. Never applies one.
 *
 * `incorrect_category` on a `marketing` template proposes `utility` and vice
 * versa, because that is the pair Meta actually disagrees with people about.
 * Everything else proposes nothing but "read this and edit" — a generated
 * rewrite of a bank's message, resubmitted on a human's behalf, is exactly the
 * automation this slice refuses.
 */
export function proposeWaTemplateFix(
  reasonCode: MetaRejectionReason | null,
  currentCategory: WaTemplateCategory,
): { kind: WaProposedFixKind; summary: string; category: WaTemplateCategory | null } {
  if (reasonCode === 'incorrect_category') {
    const category: WaTemplateCategory = currentCategory === 'marketing' ? 'utility' : 'marketing';
    return {
      kind: 'recategorise',
      summary: `Meta placed this template in a different category. Resubmitting it as ${category} may be accepted — check that the content matches before approving.`,
      category,
    };
  }
  if (reasonCode === null) {
    return {
      kind: 'no_proposal',
      summary: 'Meta gave a reason Wend does not recognise. Read it in full before changing anything.',
      category: null,
    };
  }
  return {
    kind: 'edit_content',
    summary: 'Meta refused the content. Edit the template to address the reason above, then approve the resubmission.',
    category: null,
  };
}

/**
 * The request that sends a fixed template back to Meta.
 *
 * `acknowledgedReason` is not decoration: it is a person restating what Meta
 * refused, which is the cheapest possible check that the fix was read rather
 * than clicked through. The endpoint refuses a resubmission whose template still
 * fails {@link isWaTemplateSubmittable}.
 */
export const waResubmitTemplateRequestSchema = z
  .object({
    acknowledgedReason: z.string().trim().min(1).max(2000),
    document: waTemplateDocumentSchema,
  })
  .strict();
export type WaResubmitTemplateRequest = z.infer<typeof waResubmitTemplateRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Submission                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A WhatsApp template name as Meta requires it: lower snake_case. Meta refuses
 * anything else, and normalising here rather than at the Graph call means the
 * name a person sees in Wend is the name Meta knows it by.
 */
export const waTemplateNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(WA_TEMPLATE_NAME_MAX_LENGTH)
  .regex(/^[a-z0-9_]+$/, 'A WhatsApp template name may contain only lower-case letters, digits and underscores');

export const waSubmitTemplateRequestSchema = z
  .object({
    name: waTemplateNameSchema,
    document: waTemplateDocumentSchema,
  })
  .strict();
export type WaSubmitTemplateRequest = z.infer<typeof waSubmitTemplateRequestSchema>;

/** What Meta answers a create with: an id **and** a status, always both. */
export const waSubmissionResultSchema = z
  .object({
    metaTemplateId: z.string().min(1),
    metaStatus: metaTemplateStatusSchema,
    status: templateStatusSchema,
    submittedAt: isoDateTimeSchema,
  })
  .strict();
export type WaSubmissionResult = z.infer<typeof waSubmissionResultSchema>;

/* -------------------------------------------------------------------------- */
/* The read view                                                               */
/* -------------------------------------------------------------------------- */

/** One reason this template is not submittable, named where the composer points. */
export const waTemplateIssueSchema = z.object({ code: z.string(), message: z.string() }).strict();
export type WaTemplateIssue = z.infer<typeof waTemplateIssueSchema>;

/**
 * The WhatsApp half of a template, as a screen reads it.
 *
 * The document plus what Meta has said about it, plus the two derived things a
 * composer would otherwise re-derive and get subtly different:
 * `submittable`/`issues` are {@link isWaTemplateSubmittable} and
 * {@link collectWaTemplateVariableIssues}, computed once by the API so the
 * "Submit" button and the endpoint that refuses the submission cannot disagree.
 *
 * `metaStatus` is a bare string rather than {@link metaTemplateStatusSchema}:
 * it is Meta's word, stored verbatim in an open column, and a status Meta added
 * this morning must reach the screen rather than fail a response parse.
 */
export const waTemplateViewSchema = waTemplateDocumentSchema
  .extend({
    metaTemplateId: z.string().nullable().default(null),
    metaStatus: z.string().nullable().default(null),
    submittable: z.boolean(),
    issues: z.array(waTemplateIssueSchema).default([]),
    /** Present only while the template stands rejected. Carries the proposed fix. */
    rejection: waTemplateRejectionSchema.nullable().default(null),
  })
  .strict();
export type WaTemplateView = z.infer<typeof waTemplateViewSchema>;

/* -------------------------------------------------------------------------- */
/* Submission responses                                                        */
/* -------------------------------------------------------------------------- */

/** What `POST /templates/:id/submit` and `/resubmit` answer with. */
export const waSubmitTemplateResponseSchema = z
  .object({ submission: waSubmissionResultSchema })
  .strict();
export type WaSubmitTemplateResponse = z.infer<typeof waSubmitTemplateResponseSchema>;
